'use client';
import './round-results.css';

import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { Guess, PuzzleResultsState } from '@wt/app/game/_components/types';
import { GameImageElement, RoundResultsSummary } from '@wt/app/game/_elements';
import {
  getGameScore,
  getMaxScore,
  MAX_DISTANCE_SCORE,
  MAX_YEAR_SCORE,
} from '@wt/app/game/_utils/getGameScore';
import { useRoundProgress } from '@wt/game/providers/roundProgress/RoundProgressProvider';
import { SettingsStateProps } from '@wt/game/providers/settings/types';
import { getSiteMode } from '@wt/utilities/siteMode';
import Link from 'next/link';
import { memo, useEffect, useRef, useState } from 'react';
import { GameModeTag } from '../GameModeTag';
import { getScoreIcon } from '../RoundSummary';
import {
  initialiseRoundResultAnimations,
  MapDistanceMemo,
  MediaLisence,
  RoundResultsFooterNav,
} from './RoundResults';
import BotInfo from './BotInfo';

export const UsedHintPanel = ({ guessNumber }: { guessNumber: number }) => {
  let hintFeedbackUrl = '';
  if (getSiteMode() == 'whentaken-movies') {
    hintFeedbackUrl = `https://docs.google.com/forms/d/e/1FAIpQLSdb87pqioPxjR3VDuVuwfpnko5JaakTn4j-DF_pJECXnTSn4Q/viewform?entry.1768980594=${new Date().toISOString().split('T')[0]}&entry.1911299702=${guessNumber}`;
  } else {
    hintFeedbackUrl = `https://docs.google.com/forms/d/e/1FAIpQLSelt3ZRjyPFa0kSEAij0kPe8VZplgJrUSHwYn3kTMJDI6Vs0Q/viewform?entry.1768980594=${new Date().toISOString().split('T')[0]}&entry.1911299702=${guessNumber}`;
  }

  return (
    <div className="flex w-full flex-col items-center rounded-2xl bg-[#FFEECC]">
      <div className="w-full items-center justify-center bg-[#FFCC6A] px-2 py-1 text-center text-[10px] font-semibold text-[#322E38] rounded-t-2xl lg:px-3 lg:py-2 lg:text-base">
        <LightbulbOutlinedIcon htmlColor="black" className="pr-1" /> You used a
        hint
      </div>
      <div className="px-2 py-1 text-center text-[10px] text-[#4B4554] lg:px-3 lg:py-2 lg:text-base">
        <p>Did you think it was useful?</p>
        <p className="font-bold mt-2 underline">
          <Link href={hintFeedbackUrl} target={'_blank'} title="Give Feedback">
            Give feedback
          </Link>
        </p>
      </div>
    </div>
  );
};

const RoundScoreFooter = memo(function RoundScoreFooter(props: {
  guess: Guess;
  unit: string;
}) {
  const roundScores = getGameScore(props.guess, props.unit);
  const yearScore = roundScores.score.year;
  const distanceScore = roundScores.score.distance;
  const totalScore = roundScores.score.total;

  const scoreIcon = getScoreIcon(totalScore);

  return (
    <div
      className={`inline-flex w-full items-start justify-center gap-3 2xl:gap-6`}
    >
      {getSiteMode() != 'wheretaken' && (
        <div
          className="flex flex-col items-center justify-center gap-1 flex-1"
          data-animate-element="year-score-pill"
        >
          <div className="round-score-desktop">
            <div className="flex items-end">
              <span className="text-lg 2xl:text-2xl font-bold movies:dark:text-primary">
                🗓️ {yearScore}
              </span>
              <span className="text-xs 2xl:text-base font-semibold dark:text-info-content">
                /{MAX_YEAR_SCORE}
              </span>
            </div>
          </div>
        </div>
      )}

      {getSiteMode() != 'wheretaken' && (
        <div
          className="flex flex-col items-center justify-center gap-1 flex-1"
          data-animate-element="location-score-pill"
        >
          <div className="round-score-desktop">
            <div className="flex items-end">
              <span className="text-lg 2xl:text-2xl font-bold movies:dark:text-primary">
                📍 {distanceScore}
              </span>
              <span className="text-xs 2xl:text-base font-semibold dark:text-info-content">
                /{MAX_DISTANCE_SCORE}
              </span>
            </div>
          </div>
        </div>
      )}

      <div
        className="flex flex-col items-center justify-center gap-1 flex-1"
        data-animate-element="total-score-pill"
        data-score={totalScore}
      >
        <div className="round-score-desktop">
          <div className="flex items-end">
            <span className="text-lg 2xl:text-2xl font-bold movies:dark:text-primary">
              {scoreIcon} {totalScore}
            </span>
            <span className="text-xs 2xl:text-base font-semibold dark:text-info-content">
              /{getMaxScore()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

export const RoundResultsDesktop = ({
  results,
  settings,
  lastGuess,
}: {
  settings?: SettingsStateProps;
  lastGuess?: Guess;
  results: PuzzleResultsState;
}) => {
  const { gameMode, puzzleId, currentRound } = useRoundProgress();

  const layout = getSiteMode() == 'whentaken-movies' ? 'rows' : 'columns';

  //blured image for game 2025-06-11 round 3
  const isDismissed =
    typeof window !== 'undefined' &&
    localStorage.getItem('proceedModalDismissed') === 'true';

  const shouldBlurImage =
    getSiteMode() === 'whentaken' &&
    !isDismissed &&
    results?.date === '2025-06-11' &&
    results?.round === 3;

  const [bluredImage, setBluredImage] = useState(shouldBlurImage);

  useEffect(() => {
    setBluredImage(shouldBlurImage);
  }, [shouldBlurImage]);

  if (!lastGuess || !settings) return <></>;

  const isLastRound =
    results.guesses && results.guesses.length === results.puzzle.length;

  return (
    <div
      className="flex size-full flex-col"
      data-puzzle-id={puzzleId}
      data-current-round={currentRound}
      data-should-animate={!settings?.disableRoundEndAnimation}
      ref={initialiseRoundResultAnimations}
    >
      {gameMode != 'daily' && (
        <div className="mx-auto mt-6 flex w-full max-w-screen-2xl justify-start px-8">
          <GameModeTag />
        </div>
      )}
      <div
        className={`page-body mx-auto max-w-screen-2xl py-6 ${layout == 'columns' ? '' : 'page-body-rows'}`}
      >
        <div className="mainpart mainpart-results flex h-full grow flex-col">
          <div className="relative flex w-full flex-1 grow">
            <GameImageElement
              results={results}
              puzzle={lastGuess.puzzle}
              mode="results"
              blur={bluredImage}
            />
          </div>
          <MediaLisence
            info_url={lastGuess.puzzle.info_url}
            photographer={lastGuess.puzzle.photographer}
            lisence={lastGuess.puzzle.license}
            userSubmitted={lastGuess.puzzle.userSubmitted ?? false}
          />
        </div>
        <div className="side">
          <div
            className={
              'side-sub flex flex-col justify-start gap-5 overflow-y-auto ' +
              (layout == 'rows' ? '' : 'h-0 grow')
            }
          >
            {getSiteMode() != 'wheretaken' && (
              <div className="relative side-subsub w-full">
                <MapDistanceMemo guess={lastGuess} puzzle={lastGuess.puzzle} />
              </div>
            )}
            {getSiteMode() != 'wheretaken' && layout == 'columns' && (
              <RoundScoreFooter
                guess={lastGuess}
                unit={settings.distanceUnit}
              />
            )}
            {layout == 'columns' && (
              <div className="relative flex flex-col gap-5">
                <RoundResultsSummary
                  lastGuess={lastGuess}
                  unit={settings.distanceUnit}
                />
                {getSiteMode() != 'wheretaken' && (
                  <div
                    data-animate-element="scroll-to-bottom"
                    className="scroll-mb-[50px] -mt-5"
                  ></div>
                )}
                {lastGuess?.hintsUsed && (
                  <UsedHintPanel guessNumber={results?.guesses?.length ?? 0} />
                )}
                {getSiteMode() == 'whentaken' && (
                  <div data-animate-element="bot-info">
                    <BotInfo round={lastGuess.puzzle} />
                  </div>
                )}
              </div>
            )}
            {getSiteMode() == 'wheretaken' && (
              <>
                <div className="flex-1">
                  <RoundScoreFooter
                    guess={lastGuess}
                    unit={settings.distanceUnit}
                  />
                </div>
                <div className="relative side-subsub w-full">
                  <MapDistanceMemo
                    guess={lastGuess}
                    puzzle={lastGuess.puzzle}
                  />
                </div>
              </>
            )}
          </div>

          <div
            className={
              'side-right shrink relative ' +
              (layout == 'columns' ? 'pt-6' : '')
            }
          >
            {getSiteMode() == 'whentaken' && (
              <div className="pointer-events-none absolute bottom-full left-0 h-8 w-full bg-gradient-to-t from-base-100 to-transparent" />
            )}
            {layout == 'rows' && (
              <div className="flex-1 overflow-y-auto">
                <RoundScoreFooter
                  guess={lastGuess}
                  unit={settings.distanceUnit}
                />
                <div className="mt-4" />
                <RoundResultsSummary
                  lastGuess={lastGuess}
                  unit={settings.distanceUnit}
                />
                {lastGuess?.hintsUsed && (
                  <div className="my-4">
                    <UsedHintPanel
                      guessNumber={results?.guesses?.length ?? 0}
                    />
                  </div>
                )}
              </div>
            )}
            <RoundResultsFooterNav lastRound={isLastRound} />
          </div>
        </div>
      </div>
    </div>
  );
};
